import React, { useState } from 'react';
import {
  TableHead,
  TableRow,
  TableCell,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Button,
  Stack,
  Typography,
  Divider,
  Box,
  Tooltip,
} from '@mui/material';
import { Settings, DragIndicator, RestartAlt } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnDefinition, ColumnConfig } from '../hooks/useColumnConfig';

interface SortableHeaderCellProps {
  column: ColumnDefinition;
  isFirst?: boolean;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({ column, isFirst }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };

  return (
    <TableCell
      ref={setNodeRef}
      style={style}
      align={column.align || 'left'}
      sx={{
        width: column.width,
        backgroundColor: isDragging ? 'action.selected' : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'grab',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <DragIndicator fontSize="small" />
        </Box>
        <span>{column.label}</span>
      </Stack>
    </TableCell>
  );
};

interface DraggableTableHeaderProps {
  columns: ColumnDefinition[];
  visibleColumns: ColumnDefinition[];
  columnConfigs: ColumnConfig[];
  onReorder: (activeId: string, overId: string) => void;
  onToggleVisibility: (columnId: string) => void;
  onResetToDefaults: () => void;
  isDefaultOrder: boolean;
  /** Extra cell at the beginning (e.g., for expand icon) */
  leadingCell?: React.ReactNode;
  /** Extra cell at the end (e.g., for actions) */
  trailingCell?: React.ReactNode;
}

export const DraggableTableHeader: React.FC<DraggableTableHeaderProps> = ({
  columns,
  visibleColumns,
  columnConfigs,
  onReorder,
  onToggleVisibility,
  onResetToDefaults,
  isDefaultOrder,
  leadingCell,
  trailingCell,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      onReorder(active.id as string, over.id as string);
    }
  };

  const handleSettingsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <TableHead>
          <TableRow>
            {leadingCell}
            <SortableContext
              items={visibleColumns.map(c => c.id)}
              strategy={horizontalListSortingStrategy}
            >
              {visibleColumns.map((column, index) => (
                <SortableHeaderCell
                  key={column.id}
                  column={column}
                  isFirst={index === 0}
                />
              ))}
            </SortableContext>
            <TableCell align="right" sx={{ width: 48 }}>
              <Tooltip title="Configurar columnas">
                <IconButton size="small" onClick={handleSettingsClick}>
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
            {trailingCell}
          </TableRow>
        </TableHead>
      </DndContext>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleSettingsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 240 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Columnas visibles</Typography>
            {!isDefaultOrder && (
              <Button
                size="small"
                startIcon={<RestartAlt />}
                onClick={() => {
                  onResetToDefaults();
                  handleSettingsClose();
                }}
              >
                Restaurar
              </Button>
            )}
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <List dense disablePadding>
            {columns.map((column) => {
              const config = columnConfigs.find(c => c.id === column.id);
              const isVisible = config?.visible !== false;

              return (
                <ListItem
                  key={column.id}
                  dense
                  button
                  onClick={() => onToggleVisibility(column.id)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={isVisible}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText primary={column.label} />
                </ListItem>
              );
            })}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Arrastrá las columnas en la tabla para reordenarlas
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default DraggableTableHeader;
